import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';

const logoUrl =
  'https://storage.readdy-site.link/project_files/fb9a032b-f905-4139-b305-3a12a0952c6a/59b69cfd-0953-4f81-8590-a303a1311dbd_compressed_unnamed.webp';

interface Channel {
  name: string;
  desc: string;
  url: string;
  urlLabel: string;
}

const bookstores: Channel[] = [
  {
    name: '博客來',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.books.com.tw/web/sys_puballb/books/?pubid=daiho45',
    urlLabel: 'books.com.tw',
  },
  {
    name: '誠品',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.eslite.com/search?keyword=%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96%E4%BA%8B%E6%A5%AD%E6%9C%89%E9%99%90%E5%85%AC%E5%8F%B8',
    urlLabel: 'eslite.com',
  },
  {
    name: '金石堂',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.kingstone.com.tw/bookpublish/publist/25015/',
    urlLabel: 'kingstone.com.tw',
  },
  {
    name: '三民',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.sanmin.com.tw/search?pu=%E6%A9%9F%E6%9B%9C',
    urlLabel: 'sanmin.com.tw',
  },
  {
    name: 'momo',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.momoshop.com.tw/search/%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96?brand=%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96',
    urlLabel: 'momoshop.com.tw',
  },
  {
    name: '蝦皮',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://shopee.tw/sonbook',
    urlLabel: 'shopee.tw',
  },
  {
    name: 'TAAZE 讀冊',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.taaze.tw/rwd_searchResult.html?keyType%5B%5D=3&keyword%5B%5D=%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96',
    urlLabel: 'taaze.tw',
  },
];

const ebookPlatforms: Channel[] = [
  {
    name: 'Amazon Kindle',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.amazon.com/s?k=%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96',
    urlLabel: 'amazon.com',
  },
  {
    name: '樂天 Kobo',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.kobo.com/tw/zh',
    urlLabel: 'kobo.com',
  },
  {
    name: 'Pubu',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.pubu.com.tw/store/4193937',
    urlLabel: 'pubu.com.tw',
  },
  {
    name: 'OverDrive',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.overdrive.com/publishers/%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96',
    urlLabel: 'overdrive.com',
  },
  {
    name: 'MyBook',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://mybook.mobi/mybook/zh-TW/#',
    urlLabel: 'mybook.mobi',
  },
  {
    name: 'Hami Book',
    desc: '實際上架與購買資訊請以通路頁面為準。',
    url: 'https://www.hamibook.com.tw/Search/finish?keyword=%E6%A9%9F%E6%9B%9C%E6%96%87%E5%8C%96',
    urlLabel: 'hamibook.com.tw',
  },
];

const publisherLinks: Channel[] = [
  {
    name: '崧博出版',
    desc: '本品牌委託崧博出版經銷，官方網站由崧博出版製作。品牌經銷與合作方案詳情請直接聯繫洽詢。',
    url: 'https://sonbook.net/',
    urlLabel: 'sonbook.net',
  },
  {
    name: '崧博書籍資料庫',
    desc: '完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博書籍資料庫查詢。Catalog是書籍出版資料的查詢入口。',
    url: 'https://catalog.sonbook.net/',
    urlLabel: 'catalog.sonbook.net',
  },
];

const SITE_DOMAIN = 'https://giyao.sonbook.net';
const PAGE_URL = `${SITE_DOMAIN}/stores`;
const PAGE_TITLE = '機曜文化合作購書通路｜查找出版書籍與正式書目';
const PAGE_DESC = '查看機曜文化合作購書通路，依正式書名、作者或ISBN查找相關出版書籍。機曜文化以科普類書籍為主要出版方向。價格、版本、庫存與購買方式請以各通路頁面為準。';

function getAllChannels(): Channel[] {
  return [...bookstores, ...ebookPlatforms, ...publisherLinks];
}

function PageHeaderSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex items-center pt-14 md:pt-16 bg-background-50"
    >
      {/* Logo watermark */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0 flex items-center justify-center">
        <img
          src={logoUrl}
          alt=""
          className="w-[600px] md:w-[800px] lg:w-[1000px] h-auto object-contain opacity-[0.06] rotate-[12deg] scale-110"
        />
      </div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col items-center text-center">
          <span
            style={getRevealStyle(isVisible, 0)}
            className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label mb-8"
          >
            Stores
          </span>

          <h1
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-foreground-100 leading-[1.05] tracking-tight text-glow"
          >
            機曜文化合作購書通路
          </h1>

          <p
            style={getRevealStyle(isVisible, 140)}
            className="mt-8 text-sm text-foreground-400 leading-[1.9] max-w-[640px]"
          >
            本頁整理機曜文化網站目前已建立的合作購書通路，協助讀者依正式書名、作者或ISBN查找相關出版書籍。機曜文化（GIYAO）為機曜文化事業有限公司的對外品牌名稱，以科普類書籍為主要出版方向。完整書名、作者、ISBN與出版日期請以崧博書籍資料庫為準；價格、版本、庫存、配送及購買方式則以各合作通路頁面為準。
          </p>
        </div>
      </div>
    </section>
  );
}

function ChannelGroupSection({
  title,
  subtitle,
  index,
  items,
}: {
  title: string;
  subtitle: string;
  index: string;
  items: Channel[];
}) {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-[960px] mx-auto">
          <div
            style={getRevealStyle(isVisible, 0)}
            className="flex items-center justify-center gap-4 mb-10 md:mb-14"
          >
            <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
              {index}
            </span>
            <div className="w-12 h-px bg-primary-500/30" />
            <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
              {subtitle}
            </span>
          </div>

          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center mb-4 md:mb-6"
          >
            {title}
          </h2>

          {/* Section description */}
          {index === '01' && (
            <div
              style={getRevealStyle(isVisible, 80)}
              className="text-center max-w-[640px] mx-auto mb-12 md:mb-16"
            >
              <p className="text-sm text-foreground-400 leading-[1.9]">
                請透過下方已建立的合作購書通路查找機曜文化相關書籍。各作品在不同平台的上架狀態、版本、價格、庫存與配送方式可能不同，實際資訊請以各通路頁面為準。
              </p>
              <p className="mt-3 text-sm text-foreground-500 leading-[1.9]">
                若已知完整書名、作者或ISBN，建議直接使用通路網站的搜尋功能查找，可降低同名書籍或關鍵字不完整造成的搜尋誤差。
              </p>
            </div>
          )}

          {index !== '01' && <div className="mb-12 md:mb-16" />}

          <div className="divide-y divide-primary-500/8">
            {items.map((item, i) => (
              <div
                key={i}
                style={getRevealStyle(isVisible, i * 60 + 100)}
                className="py-8 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10 items-center"
              >
                <div className="lg:col-span-1 hidden lg:flex items-center">
                  <span className="text-3xl font-bold text-foreground-800 font-heading tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="lg:col-span-7">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-foreground-400 leading-[1.9]">
                    {item.desc}
                  </p>
                </div>

                <div className="lg:col-span-4 flex flex-col">
                  <p className="text-[11px] text-foreground-600 tracking-widest uppercase mb-2 truncate font-label">
                    {item.urlLabel}
                  </p>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="group inline-flex items-center gap-2 text-sm font-medium text-primary-400 border-b border-primary-500/30 pb-0.5 hover:text-primary-300 hover:border-primary-400 transition-colors duration-200 whitespace-nowrap w-fit"
                  >
                    前往通路查找
                    <i className="ri-arrow-right-up-line text-xs" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowToSearchSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-foreground-400 tracking-[0.25em] uppercase font-label">
            Guide
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center mb-12 md:mb-16"
        >
          如何查找機曜文化書籍？
        </h2>

        <div className="max-w-[960px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          <div style={getRevealStyle(isVisible, 120)}>
            <h3 className="font-heading text-base font-bold text-foreground-200 mb-3">
              使用完整書名搜尋
            </h3>
            <p className="text-sm text-foreground-400 leading-[1.9]">
              在合作通路的搜尋欄輸入完整書名，可降低搜尋結果過多或同名作品造成的辨識困難。
            </p>
          </div>

          <div style={getRevealStyle(isVisible, 160)}>
            <h3 className="font-heading text-base font-bold text-foreground-200 mb-3">
              使用作者名稱搜尋
            </h3>
            <p className="text-sm text-foreground-400 leading-[1.9]">
              若已知作者，可搭配書名或其他關鍵字查找相關作品。
            </p>
          </div>

          <div style={getRevealStyle(isVisible, 200)}>
            <h3 className="font-heading text-base font-bold text-foreground-200 mb-3">
              使用ISBN確認
            </h3>
            <p className="text-sm text-foreground-400 leading-[1.9]">
              ISBN可用於辨識特定出版品。若書名較長或相似作品較多，建議使用ISBN查詢。
            </p>
          </div>

          <div style={getRevealStyle(isVisible, 240)}>
            <h3 className="font-heading text-base font-bold text-foreground-200 mb-3">
              先確認正式書目
            </h3>
            <p className="text-sm text-foreground-400 leading-[1.9]">
              若無法確認完整書名、作者或ISBN，可先前往崧博出版書籍資料庫查詢正式書目，再回到合作通路查找。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingNoticeSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-foreground-400 tracking-[0.25em] uppercase font-label">
            Pricing
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <div className="max-w-[720px] mx-auto text-center">
          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            價格與優惠以通路頁面為準
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            不同合作通路可能依活動、會員資格、配送方式或平台政策調整售價與優惠，機曜文化品牌網站不保證各平台價格一致。
          </p>

          <p
            style={getRevealStyle(isVisible, 130)}
            className="mt-3 text-sm text-foreground-500 leading-[1.9]"
          >
            實際售價、折扣、優惠期限與使用條件，請以各合作通路頁面及結帳資訊為準。
          </p>
        </div>
      </div>
    </section>
  );
}

function InventoryNoticeSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-foreground-400 tracking-[0.25em] uppercase font-label">
            Inventory
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <div className="max-w-[720px] mx-auto text-center">
          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            庫存與配送資訊
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            機曜文化品牌網站不提供各合作通路的即時庫存。書籍是否有貨、預購、補貨、配送範圍及到貨時間，請以各平台頁面為準。
          </p>

          <p
            style={getRevealStyle(isVisible, 130)}
            className="mt-3 text-sm text-foreground-500 leading-[1.9]"
          >
            同一本書在不同通路的上架、版本與供應狀態可能不同，若特定平台無法找到，可改至其他已建立的合作通路查詢。
          </p>
        </div>
      </div>
    </section>
  );
}

function NotFoundSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-foreground-400 tracking-[0.25em] uppercase font-label">
            Help
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <div className="max-w-[720px] mx-auto text-center">
          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            找不到書籍時怎麼辦？
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            若在特定通路找不到機曜文化相關書籍，建議先確認正式書名、作者與ISBN，再嘗試其他網站已建立的合作購書通路。
          </p>

          <p
            style={getRevealStyle(isVisible, 130)}
            className="mt-3 text-sm text-foreground-500 leading-[1.9]"
          >
            完整書目資訊可前往崧博出版書籍資料庫查詢；實際上架、價格與庫存仍以各通路頁面為準。
          </p>
        </div>
      </div>
    </section>
  );
}

function CatalogRedirectSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center max-w-[720px] mx-auto">
          <div
            style={getRevealStyle(isVisible, 0)}
            className="flex items-center justify-center gap-4 mb-12 md:mb-16"
          >
            <span className="text-[10px] font-medium text-foreground-400 tracking-[0.2em] uppercase">
              Catalog
            </span>
            <div className="w-12 h-px bg-primary-500/30" />
          </div>

          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            先查詢機曜文化正式書目
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            如需確認完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢，再使用正式資料至合作購書通路搜尋。
          </p>

          <div style={getRevealStyle(isVisible, 140)} className="mt-8">
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
            >
              查詢完整書目
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SonbookRelationSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center max-w-[720px] mx-auto">
          <div
            style={getRevealStyle(isVisible, 0)}
            className="flex items-center justify-center gap-4 mb-12 md:mb-16"
          >
            <span className="text-[10px] font-medium text-foreground-400 tracking-[0.2em] uppercase">
              Relation
            </span>
            <div className="w-12 h-px bg-primary-500/30" />
          </div>

          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            機曜文化與崧博出版
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            本品牌委託崧博出版經銷，官方網站由崧博出版製作。機曜文化是具有自身出版定位的出版品牌，完整書目可前往崧博書籍資料庫查詢。
          </p>

          <div style={getRevealStyle(isVisible, 140)} className="mt-8">
            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
            >
              前往崧博出版
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandCooperationSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="flex items-center bg-background-100 py-20 md:py-28"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center max-w-[720px] mx-auto">
          <div
            style={getRevealStyle(isVisible, 0)}
            className="flex items-center justify-center gap-4 mb-12 md:mb-16"
          >
            <span className="text-[10px] font-medium text-foreground-400 tracking-[0.2em] uppercase">
              Cooperation
            </span>
            <div className="w-12 h-px bg-primary-500/30" />
          </div>

          <h2
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight mb-6"
          >
            品牌經銷與合作洽詢
          </h2>

          <p
            style={getRevealStyle(isVisible, 100)}
            className="text-sm text-foreground-500 leading-[1.9]"
          >
            如有機曜文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。
          </p>

          <p
            style={getRevealStyle(isVisible, 130)}
            className="mt-3 text-sm text-foreground-500 leading-[1.9]"
          >
            品牌經銷與合作方案詳情請直接聯繫洽詢。
          </p>

          <div style={getRevealStyle(isVisible, 170)} className="mt-8">
            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
            >
              聯絡品牌合作
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Stores() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content || '';
    const prevCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href || '';

    document.title = PAGE_TITLE;

    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = PAGE_DESC;

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = PAGE_URL;

    // --- JSON-LD: CollectionPage ---
    const collectionPageScript = document.createElement('script');
    collectionPageScript.type = 'application/ld+json';
    collectionPageScript.id = 'ld-collectionpage';
    collectionPageScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${PAGE_URL}#webpage`,
      url: PAGE_URL,
      name: '機曜文化合作購書通路',
      description: PAGE_DESC,
      inLanguage: 'zh-TW',
      isPartOf: {
        '@id': `${SITE_DOMAIN}/#website`,
      },
      about: {
        '@id': `${SITE_DOMAIN}/#organization`,
      },
      breadcrumb: {
        '@id': `${PAGE_URL}#breadcrumb`,
      },
    });
    document.head.appendChild(collectionPageScript);

    // --- JSON-LD: ItemList ---
    const allChannels = getAllChannels();
    const itemListScript = document.createElement('script');
    itemListScript.type = 'application/ld+json';
    itemListScript.id = 'ld-itemlist';
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${PAGE_URL}#store-list`,
      name: '機曜文化合作購書通路列表',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: allChannels.length,
      itemListElement: allChannels.map((ch, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: ch.name,
        url: ch.url,
      })),
    });
    document.head.appendChild(itemListScript);

    // --- JSON-LD: BreadcrumbList ---
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'ld-breadcrumb';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${PAGE_URL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首頁',
          item: `${SITE_DOMAIN}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '合作購書通路',
          item: PAGE_URL,
        },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.title = prevTitle;

      const restoredMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (restoredMeta) {
        restoredMeta.content = prevDescription;
      }

      const restoredCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (restoredCanonical) {
        restoredCanonical.href = prevCanonical;
      }

      const cp = document.getElementById('ld-collectionpage');
      if (cp) cp.remove();
      const il = document.getElementById('ld-itemlist');
      if (il) il.remove();
      const bc = document.getElementById('ld-breadcrumb');
      if (bc) bc.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeaderSection />
        <ChannelGroupSection
          title="查找機曜文化出版書籍"
          subtitle="線上書店"
          index="01"
          items={bookstores}
        />
        <ChannelGroupSection
          title="查找機曜文化出版書籍"
          subtitle="電子書平台"
          index="02"
          items={ebookPlatforms}
        />
        <ChannelGroupSection
          title="查找機曜文化出版書籍"
          subtitle="出版與書目查詢"
          index="03"
          items={publisherLinks}
        />
        <HowToSearchSection />
        <PricingNoticeSection />
        <InventoryNoticeSection />
        <NotFoundSection />
        <CatalogRedirectSection />
        <SonbookRelationSection />
        <BrandCooperationSection />
      </main>
      <Footer />
    </div>
  );
}